import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Save, ArrowLeft, Send, CheckCheck } from "lucide-react";
import { TEMPLATE } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { FiscalYearSelect } from "@/pages/BusinessUnitDetail";

const STATUS_BADGE = {
  draft: ["Draft", "bg-zinc-100 text-zinc-700 border-zinc-300"],
  pending_owner: ["Pending BU Owner approval", "bg-amber-100 text-amber-900 border-amber-300"],
  pending_super: ["Pending Super Admin sign-off", "bg-violet-100 text-violet-900 border-violet-300"],
  approved: ["Locked for fiscal year", "bg-emerald-100 text-emerald-900 border-emerald-300"],
};

export default function TemplateBuilder() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [depts, setDepts] = useState([]);

  useEffect(() => {
    (async () => {
      const [t, d] = await Promise.all([api.get(`/templates/${id}`), api.get("/departments")]);
      setTpl(t.data); setDepts(d.data);
    })();
  }, [id]);

  if (!tpl) return <div className="text-sm text-zinc-500">Loading…</div>;
  const locked = tpl.status === "approved";
  const editable = !locked && (user?.role === "super_admin" || user?.role === "bu_owner" || (user?.role === "bu_manager" && tpl.status === "draft"));

  const updateRow = (idx, patch) => {
    const rows = [...tpl.rows];
    rows[idx] = { ...rows[idx], ...patch };
    setTpl({ ...tpl, rows });
  };

  const addRow = () => {
    setTpl({ ...tpl, rows: [...tpl.rows, { perspective: "", strategic_goal: "", kpa: "", weight: 0, measurement_tool: "", comments: "" }] });
  };
  const removeRow = (idx) => {
    const rows = [...tpl.rows];
    rows.splice(idx, 1);
    setTpl({ ...tpl, rows });
  };

  const save = async () => {
    try {
      const { data } = await api.put(`/templates/${id}`, {
        name: tpl.name, department_id: tpl.department_id, business_unit_id: tpl.business_unit_id, fiscal_year: tpl.fiscal_year, rows: tpl.rows,
      });
      setTpl(data);
      toast.success("Template saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (!confirm("Submit this template for BU Owner approval? You won&apos;t be able to edit it after submission until it&apos;s rejected or approved.")) return;
    try {
      await save();
      const { data } = await api.post(`/templates/${id}/submit`);
      setTpl(data);
      toast.success("Submitted for BU Owner approval");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const ownerApprove = async () => {
    if (!confirm("Approve this template? It will be sent to Super Admin for final sign-off.")) return;
    try {
      const { data } = await api.post(`/templates/${id}/owner-approve`);
      setTpl(data);
      toast.success("Approved, awaiting Super Admin sign-off");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const superApprove = async () => {
    if (!confirm("Sign off and LOCK this template for the fiscal year? This cannot be undone.")) return;
    try {
      const { data } = await api.post(`/templates/${id}/super-approve`);
      setTpl(data);
      toast.success("Template locked");
      nav(`/templates/${id}/view`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalWeight = tpl.rows.reduce((a, r) => a + Number(r.weight || 0), 0);
  const [statusLabel, statusCls] = STATUS_BADGE[tpl.status] || ["—", ""];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => nav(-1)} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Link to={`/templates/${id}/view`} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm hover:bg-zinc-50">Preview</Link>
          {editable && (
            <button onClick={save} data-testid={TEMPLATE.saveBtn} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm hover:bg-zinc-50 flex items-center gap-1">
              <Save className="w-4 h-4" /> Save draft
            </button>
          )}
          {/* Workflow actions */}
          {tpl.status === "draft" && (user?.role === "bu_manager" || user?.role === "bu_owner" || user?.role === "super_admin") && (
            <button onClick={submit} data-testid={TEMPLATE.submitBtn} className="bg-zinc-900 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-zinc-800 flex items-center gap-2">
              <Send className="w-4 h-4" /> Submit for approval
            </button>
          )}
          {tpl.status === "pending_owner" && (user?.role === "bu_owner" || user?.role === "super_admin") && (
            <button onClick={ownerApprove} data-testid={TEMPLATE.ownerApproveBtn} className="bg-violet-700 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-violet-800 flex items-center gap-2">
              <CheckCheck className="w-4 h-4" /> Owner approve
            </button>
          )}
          {tpl.status === "pending_super" && user?.role === "super_admin" && (
            <button onClick={superApprove} data-testid={TEMPLATE.superApproveBtn} className="bg-emerald-700 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-emerald-800 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Sign-off & Lock
            </button>
          )}
        </div>
      </div>

      <div className={`border ${statusCls} px-4 py-3 mb-6 rounded-sm flex items-center justify-between`}>
        <div className="text-sm font-semibold">{statusLabel}</div>
        <div className="text-xs">
          {tpl.submitted_at && <span className="mr-3">Submitted: {new Date(tpl.submitted_at).toLocaleDateString()}</span>}
          {tpl.owner_approved_at && <span className="mr-3">Owner approved: {new Date(tpl.owner_approved_at).toLocaleDateString()}</span>}
          {tpl.super_approved_at && <span>Signed off: {new Date(tpl.super_approved_at).toLocaleDateString()}</span>}
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-6 mb-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Template name</label>
            <input disabled={!editable} className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm disabled:bg-zinc-50" value={tpl.name} onChange={(e) => setTpl({...tpl, name: e.target.value})} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Position Profile</label>
            <select disabled={!editable} className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white disabled:bg-zinc-50" value={tpl.department_id} onChange={(e) => setTpl({...tpl, department_id: e.target.value})}>
              {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          {editable ? (
            <FiscalYearSelect value={tpl.fiscal_year} onChange={(fy) => setTpl({...tpl, fiscal_year: fy})} />
          ) : (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Fiscal year</label>
              <input disabled className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-zinc-50" value={tpl.fiscal_year} readOnly />
            </div>
          )}
        </div>
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-semibold text-zinc-700">KPA rows</div>
          <div className="flex items-center gap-3">
            <div className={`text-xs ${Math.abs(totalWeight - 100) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>Total weight: {totalWeight}% {Math.abs(totalWeight - 100) < 0.01 ? "✓" : "(should be 100%)"}</div>
            {editable && (
              <button onClick={addRow} data-testid={TEMPLATE.addRowBtn} className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-sm flex items-center gap-1 hover:bg-zinc-800">
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-xs" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "72px" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "20%" }} />
            {editable && <col style={{ width: "36px" }} />}
          </colgroup>
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-2 py-2">Perspective</th>
              <th className="text-left px-2 py-2">KPA Strategic Goal</th>
              <th className="text-left px-2 py-2">KPA</th>
              <th className="text-left px-2 py-2">Weight %</th>
              <th className="text-left px-2 py-2">Measurement Tool</th>
              <th className="text-left px-2 py-2">Comments</th>
              {editable && <th></th>}
            </tr>
          </thead>
          <tbody>
            {tpl.rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-200 align-top">
                <td className="px-1.5 py-2">
                  <textarea
                    disabled={!editable}
                    rows={2}
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 resize-y leading-snug"
                    value={r.perspective}
                    onChange={(e) => updateRow(i, { perspective: e.target.value })}
                    placeholder="e.g. Financial Impact"
                  />
                </td>
                <td className="px-1.5 py-2">
                  <textarea
                    disabled={!editable}
                    rows={2}
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 resize-y leading-snug"
                    value={r.strategic_goal}
                    onChange={(e) => updateRow(i, { strategic_goal: e.target.value })}
                  />
                </td>
                <td className="px-1.5 py-2">
                  <textarea
                    disabled={!editable}
                    rows={2}
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 resize-y leading-snug"
                    value={r.kpa}
                    onChange={(e) => updateRow(i, { kpa: e.target.value })}
                  />
                </td>
                <td className="px-1.5 py-2">
                  <input
                    disabled={!editable}
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 tabular-nums"
                    value={r.weight}
                    onChange={(e) => updateRow(i, { weight: Number(e.target.value) })}
                  />
                </td>
                <td className="px-1.5 py-2">
                  <textarea
                    disabled={!editable}
                    rows={2}
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 resize-y leading-snug"
                    value={r.measurement_tool}
                    onChange={(e) => updateRow(i, { measurement_tool: e.target.value })}
                  />
                  {(r.target || r.frequency || r.source_of_truth || editable) && (
                    <div className="mt-1 space-y-1">
                      <input disabled={!editable} placeholder="Target"
                        className="w-full border border-zinc-200 px-2 py-1 rounded-sm text-[11px] disabled:bg-zinc-50"
                        value={r.target || ""}
                        onChange={(e) => updateRow(i, { target: e.target.value })} />
                      <input disabled={!editable} placeholder="Frequency"
                        className="w-full border border-zinc-200 px-2 py-1 rounded-sm text-[11px] disabled:bg-zinc-50"
                        value={r.frequency || ""}
                        onChange={(e) => updateRow(i, { frequency: e.target.value })} />
                      <input disabled={!editable} placeholder="Source of truth"
                        className="w-full border border-zinc-200 px-2 py-1 rounded-sm text-[11px] disabled:bg-zinc-50"
                        value={r.source_of_truth || ""}
                        onChange={(e) => updateRow(i, { source_of_truth: e.target.value })} />
                    </div>
                  )}
                </td>
                <td className="px-1.5 py-2">
                  <textarea
                    disabled={!editable}
                    rows={2}
                    className="w-full border border-zinc-300 px-2 py-1.5 rounded-sm text-xs disabled:bg-zinc-50 resize-y leading-snug"
                    value={r.comments}
                    onChange={(e) => updateRow(i, { comments: e.target.value })}
                  />
                </td>
                {editable && (
                  <td className="px-1 py-2 text-right">
                    <button onClick={() => removeRow(i)} className="text-red-600 hover:text-red-800" title="Remove row">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {tpl.rows.length === 0 && (
              <tr><td colSpan={editable ? 7 : 6} className="px-3 py-10 text-center text-zinc-500">No rows yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
